import React, { FC, ReactNode, RefObject } from 'react';
import ReactDOM from 'react-dom';
import { Transition } from 'react-transition-group';
import classNames from 'classnames';
import Markdown from 'markdown-to-jsx';

import { Icon, IconSize } from 'app/design-system/components/Icon';

import { ANIMATION_DURATION, ANIMATION_STYLES, HINT_DELAY_DURATION } from './utils';

import styles from './Popper.module.scss';

type PopperBodyType = {
  isOpen: boolean;
  isTooltip: boolean;
  align: 'left' | 'center';
  content: ReactNode;
  popperElement: RefObject<HTMLDivElement>;
  hide: () => void;
  popperClassName?: string;
};

const PopperBody: FC<PopperBodyType> = ({
  isOpen,
  isTooltip,
  align,
  content,
  popperElement,
  hide,
  popperClassName,
}) => {
  const withDelay = !isTooltip && isOpen;
  const transition = `opacity ${ANIMATION_DURATION}ms ease ${withDelay ? HINT_DELAY_DURATION : 0}ms`;

  const _popperClassName = classNames(
    styles.popper,
    { [styles.hint]: !isTooltip },
    popperClassName,
  );

  const _popperContentClassName = classNames(
    styles.popperContent,
    { [styles.hintContent]: !isTooltip },
  );

  return ReactDOM.createPortal(
    <Transition in={isOpen}
                timeout={ANIMATION_DURATION}>
      {(state): JSX.Element => (
        <div ref={popperElement}
             role="tooltip"
             style={{
               transition,
               ...ANIMATION_STYLES[state as keyof typeof ANIMATION_STYLES],
             }}
             className={_popperClassName}>
          <div className={_popperContentClassName}
               style={{ textAlign: align }}>
            {typeof content === 'string' ? (
              <Markdown>
                {content}
              </Markdown>
            ) : content}
            {isTooltip && (
              <button className={styles.closeButton}
                      type="button"
                      onClick={hide}>
                <Icon icon="close-tiny"
                      size={IconSize.SMALL} />
              </button>
            )}
          </div>
          <div data-popper-arrow="true"
               className={styles.arrow} />
        </div>
      )}
    </Transition>,
    document.body,
  );
};

export default PopperBody;
